import React, { useEffect, useRef, useState } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Col, Row } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { Text } from '@amzn/stencil-react-components/text'
import {
    IconDownloadSmall as IconDownload,
    IconRefreshSmall as IconRefresh,
    IconStopSmall as IconStop,
} from '@amzn/stencil-react-icons'

import { ModalContent } from 'src/components/ModalContent'
import { SPEAKER_STYLE, SpeakerIndicator } from 'src/components/virtual-candidate/SpeakerIndicator'
import { getCompetencyTypeName, IscreenCompetencyDTO } from 'src/models/dto/activities/IScreenDTO'
import {
    isActiveStatus,
    VirtualCandidateProgress,
    VirtualCandidateSessionStatus,
    VirtualCandidateUtterance,
} from 'src/services/backend/VirtualCandidateService'
import { VirtualCandidateJob } from 'src/services/virtual-candidate/sessionRequest'
import {
    VirtualCandidateAudio,
    VirtualCandidateState,
} from 'src/services/virtual-candidate/VirtualCandidateSessionController'

export interface VirtualCandidateSessionViewProps {
    state: VirtualCandidateState
    job: VirtualCandidateJob
    /** The page's competency slots when the run started; Delivery fills each one through CIS. */
    competencies: IscreenCompetencyDTO[]
    /** The persona the candidate plays, e.g. "Nervous candidate". */
    personaName?: string
    onEndInterview: () => void
    /** Listens to the running interview again after the live audio dropped or was stopped. */
    onReconnectAudio: () => void
    /** Stops the live audio without ending the interview. */
    onDisconnectAudio: () => void
    onClose: () => void
}

const STATUS_LABEL: Record<VirtualCandidateSessionStatus, string> = {
    STARTING: 'Starting the interview',
    RUNNING: 'Live',
    COMPLETED: 'Completed',
    STOPPED: 'Ended early',
    FAILED: 'Failed',
}

const STATUS_COLOR: Record<VirtualCandidateSessionStatus | 'ERROR', string> = {
    STARTING: '#8c8c8c',
    RUNNING: '#d13212',
    COMPLETED: '#1d8102',
    STOPPED: '#5f6b7a',
    FAILED: '#d13212',
    ERROR: '#d13212',
}

const SECONDS_PER_MINUTE = 60
const MS_PER_SECOND = 1000

export const formatDuration = (totalSeconds: number): string => {
    const seconds = Math.max(0, Math.floor(totalSeconds))
    const minutes = Math.floor(seconds / SECONDS_PER_MINUTE)
    return `${minutes}:${String(seconds % SECONDS_PER_MINUTE).padStart(2, '0')}`
}

/** Seconds since the interview started, ticking while it runs and frozen once it ended. */
const useElapsedSeconds = (startedAt?: string | null, endedAt?: string | null): number => {
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        if (!startedAt || endedAt) return undefined
        const timer = setInterval(() => setNow(Date.now()), MS_PER_SECOND)
        return () => clearInterval(timer)
    }, [startedAt, endedAt])
    if (!startedAt) return 0
    const end = endedAt ? Date.parse(endedAt) : now
    return (end - Date.parse(startedAt)) / MS_PER_SECOND
}

const describeRequest = (job: VirtualCandidateJob, personaName?: string): string => {
    const who = personaName ? `An AI candidate (${personaName})` : 'An AI candidate'
    const role = job.jobRole.trim() ? ` as a ${job.jobRole.trim()}` : ''
    return `${who} takes this iScreen interview for requisition ${job.requisitionId.trim()}${role}.`
}

const PERCENT = 100

/** "Technical/Functional · 7:00": a competency slot's type and time limit, as far as the page has them. */
export const describeCompetency = (competency: IscreenCompetencyDTO): string =>
    [
        getCompetencyTypeName(competency.competencyType) ?? 'Competency type not set',
        competency.iscreenCompetencyTimeLimitSeconds
            ? formatDuration(competency.iscreenCompetencyTimeLimitSeconds)
            : undefined,
    ]
        .filter(Boolean)
        .join(' · ')

/**
 * The index of the competency being asked about. GAS reports the share of competencies completed,
 * so the current one is the next after those done.
 */
export const currentCompetencyIndex = (
    progress: VirtualCandidateProgress,
    competencies: number
): number => Math.min(Math.round((progress.percent / PERCENT) * competencies), competencies - 1)

/** "Practice round", or "Competency 2 of 3 · Technical/Functional · 7:00". */
export const describeProgress = (
    progress: VirtualCandidateProgress,
    competencies: IscreenCompetencyDTO[]
): string => {
    if (progress.phase === 'MINI_INTERVIEW') return 'Practice round'
    if (competencies.length === 0) return 'Interview'
    const index = currentCompetencyIndex(progress, competencies.length)
    return `Competency ${index + 1} of ${competencies.length} · ${describeCompetency(
        competencies[index]
    )}`
}

/**
 * One color per competency, cycled when a page has more: a light track for the whole slot and a
 * dark strip of the same hue for the time it has used.
 */
export const SEGMENT_COLORS = [
    { track: '#cfe8ea', elapsed: '#00838f' }, // teal
    { track: '#e3dcf7', elapsed: '#5b4bdb' }, // violet
    { track: '#fde2c8', elapsed: '#c45f00' }, // orange
    { track: '#d6eccd', elapsed: '#2e7d32' }, // green
    { track: '#f7d4de', elapsed: '#c2185b' }, // pink
    { track: '#d4e3f7', elapsed: '#1565c0' }, // blue
] as const

export const segmentColor = (index: number) => SEGMENT_COLORS[index % SEGMENT_COLORS.length]

type SegmentState = 'done' | 'current' | 'upcoming'

/** When each competency (by index) started and, once the interview moved on, ended; epoch ms. */
type CompetencyTimes = Record<number, { start: number; end?: number }>

/**
 * Times each competency as Designer sees it: it starts when it becomes the current one and ends
 * when the next one does or the interview ends. Ticks every second while one runs.
 */
export const useCompetencyTimes = (current: number | undefined, ended: boolean) => {
    const [times, setTimes] = useState<CompetencyTimes>({})
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        setTimes((previous) => {
            const at = Date.now()
            const next: CompetencyTimes = { ...previous }
            let changed = false
            for (const [key, time] of Object.entries(next)) {
                if (time.end === undefined && (ended || Number(key) !== current)) {
                    next[Number(key)] = { ...time, end: at }
                    changed = true
                }
            }
            if (!ended && current !== undefined && !next[current]) {
                next[current] = { start: at }
                changed = true
            }
            return changed ? next : previous
        })
        setNow(Date.now())
    }, [current, ended])
    useEffect(() => {
        if (ended || current === undefined) return undefined
        const timer = setInterval(() => setNow(Date.now()), MS_PER_SECOND)
        return () => clearInterval(timer)
    }, [current, ended])
    return { times, now }
}

/**
 * The share of a competency's time limit it has used, 0 to 1. A competency that ended early keeps
 * the share it reached. One Designer never saw start (the modal opened later) shows as full once done.
 */
export const elapsedShare = (
    time: { start: number; end?: number } | undefined,
    limitSeconds: number,
    state: SegmentState,
    now: number
): number => {
    if (!time) return state === 'done' ? 1 : 0
    const seconds = ((time.end ?? now) - time.start) / MS_PER_SECOND
    return Math.min(Math.max(seconds / limitSeconds, 0), 1)
}

/**
 * Each slot's share of the bar: its time limit, or the average of the others when it has none, so
 * a slot without a time limit still shows.
 */
export const segmentWeights = (competencies: IscreenCompetencyDTO[]): number[] => {
    const known = competencies
        .map((competency) => competency.iscreenCompetencyTimeLimitSeconds)
        .filter((seconds): seconds is number => Boolean(seconds && seconds > 0))
    const fallback = known.length > 0 ? known.reduce((sum, s) => sum + s, 0) / known.length : 1
    return competencies.map((competency) =>
        competency.iscreenCompetencyTimeLimitSeconds &&
        competency.iscreenCompetencyTimeLimitSeconds > 0
            ? competency.iscreenCompetencyTimeLimitSeconds
            : fallback
    )
}

/**
 * A completed interview has every competency done. One that ended early (stopped or failed) keeps
 * the competencies it reached as done and the rest upcoming.
 */
const segmentState = (
    index: number,
    current: number | undefined,
    outcome: InterviewOutcome,
    reached: boolean
): SegmentState => {
    if (outcome === 'COMPLETED') return 'done'
    if (outcome !== 'RUNNING') return reached ? 'done' : 'upcoming'
    if (current === undefined || index > current) return 'upcoming'
    return index < current ? 'done' : 'current'
}

/** RUNNING while the session is active, otherwise how it ended. */
type InterviewOutcome = 'RUNNING' | 'COMPLETED' | 'STOPPED' | 'FAILED'

const OUTCOME_HEADING: Record<Exclude<InterviewOutcome, 'RUNNING'>, string> = {
    COMPLETED: 'Interview ended',
    STOPPED: 'Interview ended early',
    FAILED: 'Interview failed',
}

/**
 * The interview's progress as one bar split into the page's competency slots, each as wide as its
 * share of the time. Each slot is a light track with a dark strip for the share of its time limit
 * used so far; a competency that ends early keeps its strip where it stopped. The one being asked
 * about is outlined, and the line above names it. The names come from CIS and aren't known here,
 * so each shows its type.
 */
const Progress = ({
    progress,
    competencies,
    outcome,
}: {
    progress: VirtualCandidateProgress | null
    competencies: IscreenCompetencyDTO[]
    outcome: InterviewOutcome
}) => {
    const ended = outcome !== 'RUNNING'
    // The service stops reporting progress once the session ends, so keep the last percent and
    // the furthest competency seen.
    const [lastPercent, setLastPercent] = useState(0)
    const [furthest, setFurthest] = useState<number>()
    const live = progress?.phase === 'INTERVIEW' && competencies.length > 0 ? progress : undefined
    useEffect(() => {
        if (!live) return
        setLastPercent(Math.round(live.percent))
        const index = currentCompetencyIndex(live, competencies.length)
        setFurthest((previous) => Math.max(previous ?? index, index))
    }, [live, competencies.length])
    const percent =
        outcome === 'COMPLETED'
            ? PERCENT
            : progress?.phase === 'INTERVIEW'
            ? Math.round(progress.percent)
            : ended
            ? lastPercent
            : 0
    const current =
        progress?.phase === 'INTERVIEW' && competencies.length > 0
            ? currentCompetencyIndex(progress, competencies.length)
            : undefined
    const heading = ended
        ? OUTCOME_HEADING[outcome]
        : progress
        ? describeProgress(progress, competencies)
        : 'Waiting for the interview to start'
    const weights = segmentWeights(competencies)
    const { times, now } = useCompetencyTimes(current, ended)
    return (
        <Col gridGap='S100' dataTestId='virtual-candidate-progress'>
            <Row justifyContent='space-between'>
                <Text fontSize='T200' fontWeight='bold' dataTestId='virtual-candidate-current'>
                    {heading}
                </Text>
                <Text fontSize='T200' color='neutral70'>
                    {percent}% complete
                </Text>
            </Row>
            <div
                role='progressbar'
                aria-label='Interview progress'
                aria-valuemin={0}
                aria-valuemax={PERCENT}
                aria-valuenow={percent}
                aria-valuetext={`${percent}% complete. ${heading}`}
                style={{ display: 'flex', gap: 3 }}
            >
                {competencies.map((competency, index) => {
                    const state = segmentState(
                        index,
                        current,
                        outcome,
                        furthest !== undefined && index <= furthest
                    )
                    const share = elapsedShare(times[index], weights[index], state, now)
                    const color = segmentColor(index)
                    const description = `Competency ${index + 1}: ${describeCompetency(competency)}`
                    return (
                        <div
                            key={competency.id}
                            data-test-id={`virtual-candidate-segment-${index + 1}`}
                            data-state={state}
                            title={description}
                            style={{ flex: `${weights[index]} 1 0`, minWidth: 0 }}
                        >
                            <div
                                style={{
                                    height: 10,
                                    borderRadius: 5,
                                    overflow: 'hidden',
                                    background: color.track,
                                    boxShadow:
                                        state === 'current'
                                            ? `0 0 0 2px ${color.elapsed}`
                                            : undefined,
                                }}
                            >
                                <div
                                    data-test-id={`virtual-candidate-segment-${index + 1}-elapsed`}
                                    style={{
                                        width: `${Math.round(share * PERCENT)}%`,
                                        height: '100%',
                                        background: color.elapsed,
                                        transition: 'width 1s linear',
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    marginTop: 4,
                                    fontSize: 12,
                                    color: state === 'upcoming' ? '#5f6b7a' : '#16191f',
                                    fontWeight: state === 'current' ? 'bold' : 'normal',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {describeCompetency(competency)}
                            </div>
                        </div>
                    )
                })}
            </div>
        </Col>
    )
}

const AUDIO_STATUS: Record<VirtualCandidateAudio, string> = {
    playing: 'Listening live',
    off: 'Not listening; the interview continues',
    failed: 'The live audio dropped; the interview continues',
}

/**
 * Stops the live audio, or reconnects to it after it dropped or was stopped. Reconnecting joins
 * what is being said now; the interview itself keeps running either way.
 */
const AudioControl = ({
    audio,
    onReconnect,
    onDisconnect,
}: {
    audio: VirtualCandidateAudio
    onReconnect: () => void
    onDisconnect: () => void
}) => {
    const playing = audio === 'playing'
    const label = playing ? 'Stop listening' : 'Reconnect to the live audio'
    return (
        <Row gridGap='S200' alignItems='center' justifyContent='center'>
            <Button
                dataTestId={
                    playing ? 'virtual-candidate-audio-stop' : 'virtual-candidate-audio-reconnect'
                }
                variant={ButtonVariant.Tertiary}
                aria-label={label}
                title={label}
                icon={playing ? <IconStop aria-hidden /> : <IconRefresh aria-hidden />}
                onClick={playing ? onDisconnect : onReconnect}
            />
            <Text fontSize='T200' color='neutral70' dataTestId='virtual-candidate-audio-status'>
                {AUDIO_STATUS[audio]}
            </Text>
        </Row>
    )
}

/** "Irene: …" per line, practice turns marked, as the transcript shows them. */
export const transcriptText = (utterances: VirtualCandidateUtterance[]): string =>
    utterances
        .map(
            (utterance) =>
                `${SPEAKER_STYLE[utterance.speaker].name}: ${
                    utterance.phase === 'MINI_INTERVIEW' ? '(practice) ' : ''
                }${utterance.text}`
        )
        .join('\n') + '\n'

/** <sessionId>.txt once the interview ended; <sessionId>_incomplete.txt while it still runs. */
export const transcriptFileName = (sessionId: string, inProgress: boolean): string =>
    `${sessionId}${inProgress ? '_incomplete' : ''}.txt`

const downloadTranscript = (fileName: string, utterances: VirtualCandidateUtterance[]) => {
    const link = document.createElement('a')
    link.href = URL.createObjectURL(
        new Blob([transcriptText(utterances)], { type: 'text/plain;charset=utf-8' })
    )
    link.download = fileName
    // Firefox only follows a download link that is in the document.
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(link.href)
}

const Transcript = ({ utterances }: { utterances: VirtualCandidateUtterance[] }) => {
    const endRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        endRef.current?.scrollIntoView?.({ block: 'nearest' })
    }, [utterances.length])
    if (utterances.length === 0) {
        return (
            <Text color='neutral70'>
                The transcript appears here as Irene and the candidate speak.
            </Text>
        )
    }
    return (
        <div
            data-test-id='virtual-candidate-transcript'
            style={{ maxHeight: 220, overflowY: 'auto', paddingRight: 8 }}
        >
            <Col gridGap='S200'>
                {utterances.map((utterance) => (
                    <Row
                        key={`${utterance.at}-${utterance.speaker}`}
                        gridGap='S200'
                        alignItems='flex-start'
                    >
                        <span
                            style={{
                                flex: '0 0 84px',
                                fontWeight: 600,
                                color: SPEAKER_STYLE[utterance.speaker].color,
                            }}
                        >
                            {SPEAKER_STYLE[utterance.speaker].name}
                        </span>
                        <Text>
                            {utterance.phase === 'MINI_INTERVIEW' && (
                                <span style={{ color: '#5f6b7a' }}>(practice) </span>
                            )}
                            {utterance.text || '…'}
                        </Text>
                    </Row>
                ))}
                <div ref={endRef} />
            </Col>
        </div>
    )
}

/** The modal body for one virtual candidate run: visualizer, status, transcript and controls. */
export const VirtualCandidateSessionView = ({
    state,
    job,
    competencies,
    personaName,
    onEndInterview,
    onReconnectAudio,
    onDisconnectAudio,
    onClose,
}: VirtualCandidateSessionViewProps) => {
    const { session, phase } = state
    const elapsed = useElapsedSeconds(session?.startedAt, session?.endedAt)
    const status = session?.status ?? 'STARTING'
    const statusKey = phase === 'error' ? 'ERROR' : status
    const statusText =
        phase === 'error'
            ? 'Could not start the interview'
            : state.stopping
            ? 'Ending: Irene is closing the interview'
            : STATUS_LABEL[status]
    const canEnd = phase === 'live' && Boolean(session) && isActiveStatus(status) && !state.stopping
    const failure = status === 'FAILED' ? session?.failureReason : undefined

    return (
        <ModalContent
            titleText='Virtual candidate interview'
            maxWidth='640px'
            buttons={[
                ...(canEnd || state.stopping
                    ? [
                          <Button
                              key='end'
                              dataTestId='virtual-candidate-end'
                              onClick={onEndInterview}
                              aria-disabled={!canEnd}
                              isDestructive
                          >
                              End interview
                          </Button>,
                      ]
                    : []),
                <Button
                    key='close'
                    dataTestId='virtual-candidate-close'
                    variant={ButtonVariant.Primary}
                    onClick={onClose}
                >
                    Close
                </Button>,
            ]}
        >
            <Col gridGap='S400'>
                <Text>{describeRequest(job, personaName)}</Text>
                {state.error && (
                    <MessageBanner
                        type={MessageBannerType.Error}
                        dataTestId='virtual-candidate-error'
                    >
                        {state.error}
                    </MessageBanner>
                )}
                {failure && (
                    <MessageBanner
                        type={MessageBannerType.Error}
                        dataTestId='virtual-candidate-failure'
                    >
                        {failure}
                    </MessageBanner>
                )}
                <Row justifyContent='space-between' alignItems='center'>
                    <Row gridGap='S200' alignItems='center' dataTestId='virtual-candidate-status'>
                        <span
                            aria-hidden
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: STATUS_COLOR[statusKey],
                                boxShadow:
                                    phase === 'live' && status === 'RUNNING'
                                        ? `0 0 0 4px ${STATUS_COLOR.RUNNING}33`
                                        : undefined,
                            }}
                        />
                        <Text fontWeight='bold'>{statusText}</Text>
                    </Row>
                    <Text dataTestId='virtual-candidate-elapsed' fontSize='T200'>
                        {formatDuration(elapsed)}
                    </Text>
                </Row>
                {(state.progress || competencies.length > 0) && (
                    <Progress
                        progress={state.progress}
                        competencies={competencies}
                        outcome={status === 'STARTING' || status === 'RUNNING' ? 'RUNNING' : status}
                    />
                )}
                <SpeakerIndicator
                    speaking={phase === 'live' ? state.speaking : null}
                    analyser={state.analyser}
                />
                {phase === 'live' && session && isActiveStatus(status) && (
                    <AudioControl
                        audio={state.audio}
                        onReconnect={onReconnectAudio}
                        onDisconnect={onDisconnectAudio}
                    />
                )}
                <Col gridGap='S200'>
                    <Row justifyContent='space-between' alignItems='center'>
                        <Text fontWeight='bold'>Transcript</Text>
                        {session && state.utterances.length > 0 && (
                            <Button
                                dataTestId='virtual-candidate-transcript-download'
                                variant={ButtonVariant.Tertiary}
                                aria-label='Download the transcript'
                                title='Download the transcript'
                                icon={<IconDownload aria-hidden />}
                                onClick={() =>
                                    downloadTranscript(
                                        transcriptFileName(
                                            session.sessionId,
                                            isActiveStatus(status)
                                        ),
                                        state.utterances
                                    )
                                }
                            />
                        )}
                    </Row>
                    <Transcript utterances={state.utterances} />
                </Col>
            </Col>
        </ModalContent>
    )
}
